import { useCallback, useEffect, useState } from 'react'
import type { PushProvider } from 'stream-chat'

import { useChatConnection } from '@/context/ChatConnectionContext'
import { useFeedConnection } from '@/context/FeedConnectionContext'
import { useNativeBridge } from '@/context/NativeBridgeContext'
import {
    hasAppliedDefaultCategoryLevels,
    markDefaultCategoryLevelsApplied,
    saveNotificationCategoryLevels,
} from '@/helpers/notificationCategoryLevels'
import { DEFAULT_NOTIFICATION_CATEGORY_LEVELS, NotificationPermissionStatus } from '@/types'

import { useNotificationPermission } from './useNotificationPermission'

interface UsePushRegistrationResult {
    bridgeAvailable: boolean
    status: NotificationPermissionStatus | null
    permissionLoading: boolean
    enabled: boolean
    loading: boolean
    enable: () => Promise<boolean>
    disable: () => Promise<void>
}

const PUSH_PROVIDER: PushProvider = 'firebase'
const PUSH_PROVIDER_NAME = 'valk-talk'

// Stream uses the push token as the device id, so the token identifies this device among all the user's devices.
const isCurrentDevice = (
    device: { id: string, push_provider: string, push_provider_name?: string },
    token: string,
): boolean => (
    device.id === token && device.push_provider === PUSH_PROVIDER && device.push_provider_name === PUSH_PROVIDER_NAME
)

export const usePushRegistration = (): UsePushRegistrationResult => {
    const bridge = useNativeBridge()
    const { client: chatClient, userId } = useChatConnection()
    const { client: feedsClient } = useFeedConnection()
    const {
        bridgeAvailable, status, loading: permissionLoading, request: requestPermission,
    } = useNotificationPermission()

    const [enabled, setEnabled] = useState(false)
    const [registrationLoading, setRegistrationLoading] = useState(bridgeAvailable)

    useEffect(() => {
        if (! chatClient || ! feedsClient || ! bridgeAvailable || permissionLoading) {
            return undefined
        }

        if (status !== 'granted') {
            // Can't move this into an async callback without a stale-state race when permission is revoked externally.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setEnabled(false)
            setRegistrationLoading(false)
            return undefined
        }

        let cancelled = false

        const hydrate = async (): Promise<void> => {
            try {
                const token = await bridge.getPushToken()
                const { devices } = token ? await chatClient.getDevices() : { devices: [] }
                const isRegistered = !! token && !! devices?.some(device => isCurrentDevice(device, token))

                if (! cancelled) {
                    setEnabled(isRegistered)
                    setRegistrationLoading(false)
                }
            } catch (error) {
                console.error(error)

                if (! cancelled) {
                    setRegistrationLoading(false)
                }
            }
        }

        void hydrate()

        return () => {
            cancelled = true
        }
    }, [bridge, chatClient, feedsClient, bridgeAvailable, status, permissionLoading])

    const enable = useCallback(async (): Promise<boolean> => {
        if (! chatClient || ! feedsClient || ! bridgeAvailable) {
            return false
        }

        const result = await requestPermission()

        if (result !== 'granted') {
            setEnabled(false)
            return false
        }

        const token = await bridge.getPushToken()

        if (! token) {
            setEnabled(false)
            return false
        }

        await Promise.all([
            chatClient.addDevice(token, PUSH_PROVIDER, undefined, PUSH_PROVIDER_NAME),
            feedsClient.createDevice({ id: token, push_provider: PUSH_PROVIDER, push_provider_name: PUSH_PROVIDER_NAME }),
        ])

        if (! hasAppliedDefaultCategoryLevels(userId)) {
            try {
                await saveNotificationCategoryLevels(chatClient, feedsClient, userId, DEFAULT_NOTIFICATION_CATEGORY_LEVELS)
                markDefaultCategoryLevelsApplied(userId)
            } catch (error) {
                console.error(error)
            }
        }

        setEnabled(true)

        return true
    }, [bridge, chatClient, feedsClient, bridgeAvailable, requestPermission, userId])

    const disable = useCallback(async (): Promise<void> => {
        if (! chatClient || ! feedsClient || ! bridgeAvailable) {
            return
        }

        const token = await bridge.getPushToken()

        // Without a token (e.g. permission revoked in the OS) this device can't be identified, nor receive pushes.
        if (token) {
            const results = await Promise.allSettled([
                chatClient.removeDevice(token),
                feedsClient.deleteDevice({ id: token }),
            ])

            results.forEach((result) => {
                if (result.status === 'rejected') {
                    console.error(result.reason)
                }
            })
        }

        setEnabled(false)
    }, [bridge, chatClient, feedsClient, bridgeAvailable])

    return {
        bridgeAvailable,
        status,
        permissionLoading,
        enabled,
        loading: permissionLoading || registrationLoading,
        enable,
        disable,
    }
}
